import React, { useState, useEffect, useRef } from 'react'
import '../styles/FacturasEditor.css'
import { GuiaCard } from './GuiaCard'
import FacturasAttachments from './FacturasAttachments'

export interface FacturasEditorProps {
  apiBaseUrl?: string
  cuit?: string
  soporte?: string
  onFacturaSaved?: (facturaId: string, data: FacturaData, pdfFile: File) => void
  onError?: (error: string) => void
}

interface ComprobanteData {
  tipo: string | null
  letra: string | null
  punto_venta: string | null
  numero: string | null
  fecha: string | null
}

interface EmisorData {
  cuit: string | null
  razon_social: string | null
  condicion_iva: string | null
}

interface ReceptorData {
  cuit: string | null
}

interface ImportesData {
  neto: number | null
  iva_21: number | null
  iva_105: number | null
  otros_impuestos: number | null
  total: number | null
}

interface AfipData {
  cae: string | null
  cae_vencimiento: string | null
}

interface ItemData {
  concepto: string | null
  cantidad: number | null
  precio_unit: number | null
  subtotal: number | null
}

export interface FacturaData {
  comprobante: ComprobanteData
  emisor: EmisorData
  receptor: ReceptorData
  importes: ImportesData
  afip: AfipData
  items: ItemData[]
}

export const FacturasEditor: React.FC<FacturasEditorProps> = ({
  apiBaseUrl = 'https://doc-ia-processor.sindicalia.com.ar',
  cuit,
  soporte,
  onFacturaSaved,
  onError
}) => {
  const [api, setApi] = useState<string>(apiBaseUrl)
  const [apiStatus, setApiStatus] = useState<'ok' | 'error' | 'loading'>(
    'loading'
  )
  const [uploadStatus, setUploadStatus] = useState<{
    message: string
    type: 'success' | 'error' | 'loading' | ''
  }>({ message: '', type: '' })
  const [saveStatus, setSaveStatus] = useState<{
    message: string
    type: 'success' | 'error' | 'loading' | ''
  }>({ message: '', type: '' })
  const [showEditor, setShowEditor] = useState(false)
  const [currentFacturaId, setCurrentFacturaId] = useState<string | null>(null)
  const [currentPredData, setCurrentPredData] = useState<any>(null)
  const [currentPdfUrl, setCurrentPdfUrl] = useState<string | null>(null)
  const [items, setItems] = useState<ItemData[]>([
    { concepto: null, cantidad: null, precio_unit: null, subtotal: null }
  ])
  const [isDragging, setIsDragging] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isDataLoaded, setIsDataLoaded] = useState(false)
  const [loadingStep, setLoadingStep] = useState<
    'uploading' | 'processing' | 'extracting' | 'ready' | ''
  >('')
  const [showGuia, setShowGuia] = useState(false)
  const [showHelpMenu, setShowHelpMenu] = useState(false)
  const [validationErrors, setValidationErrors] = useState<string[]>([])
  const [duplicateError, setDuplicateError] = useState<{ message: string; docId: string } | null>(null)
  const [currentPdfFile, setCurrentPdfFile] = useState<File | null>(null)
  const [showUndoConfirm, setShowUndoConfirm] = useState(false)
  const [showSuccessPopup, setShowSuccessPopup] = useState(false)
  const [docId, setDocId] = useState<string | null>(null)

  const pdfFileRef = useRef<HTMLInputElement>(null)
  const formRef = useRef<HTMLFormElement>(null)

  // Cargar configuración inicial
  useEffect(() => {
    const savedApi = localStorage.getItem('API_BASE') || apiBaseUrl
    setApi(savedApi)
    ping(savedApi)
  }, [apiBaseUrl])

  // Ping a la API
  const ping = async (apiUrl: string = api) => {
    try {
      const r = await fetch(apiUrl + '/health')
      const data = await r.json()
      const isHealthy =
        data?.status?.toLowerCase() === 'ok' ||
        data.data?.status?.toLowerCase() === 'ok'
      setApiStatus(isHealthy ? 'ok' : 'error')
    } catch (e) {
      setApiStatus('error')
    }
  }

  // Manejar drag & drop
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    const files = e.dataTransfer.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type === 'application/pdf') {
        if (pdfFileRef.current) {
          const dataTransfer = new DataTransfer()
          dataTransfer.items.add(file)
          pdfFileRef.current.files = dataTransfer.files
        }
        // Procesar automáticamente después de drop
        handleUploadSubmit(new Event('submit') as any)
      } else {
        setUploadStatus({
          message: 'Por favor, selecciona un archivo PDF',
          type: 'error'
        })
      }
    }
  }

  const handleFileClick = () => {
    pdfFileRef.current?.click()
  }

  // Limpiar CUIT
  const cleanCuit = (cuit: string | null): string | null => {
    if (!cuit) return null
    return cuit.toString().replace(/[-\s]/g, '')
  }

  // Subir archivo
  const handleUploadSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const file = pdfFileRef.current?.files?.[0]
    if (!file) return

    // Guardar el archivo PDF en el estado
    setCurrentPdfFile(file)

    // Validar CUIT si está presente
    if (cuit) {
      try {
        const cuitNormalizado = cuit.replace(/[-\s]/g, '')
        if (!/^\d{11}$/.test(cuitNormalizado)) {
          throw new Error('El CUIT debe tener 11 dígitos numéricos')
        }
      } catch (error) {
        const errorMsg =
          error instanceof Error ? error.message : 'Error validando CUIT'
        setUploadStatus({ message: errorMsg, type: 'error' })
        if (onError) onError(errorMsg)
        return
      }
    }

    setLoadingStep('uploading')
    setUploadStatus({ message: 'Subiendo archivo...', type: 'loading' })
    setIsLoading(true)
    setIsDataLoaded(false)

    const formData = new FormData()
    formData.append('file', file)

    // Agregar CUIT normalizado si está presente
    if (cuit) {
      const cuitNormalizado = cuit.replace(/[-\s]/g, '')
      formData.append('cuit', cuitNormalizado)
    }

    try {
      console.log('Enviando petición a: ', `${api}/facturas/upload`)

      setLoadingStep('processing')
      setUploadStatus({ message: 'Procesando PDF...', type: 'loading' })

      const response = await fetch(`${api}/facturas/upload`, {
        method: 'POST',
        body: formData
      })

      console.log('Response status:', response.status, response.statusText)

      setLoadingStep('extracting')
      setUploadStatus({ message: 'Extrayendo datos...', type: 'loading' })

      const result = await response.json()
      console.log('Response body:', result)

      // Verificar primero si es un duplicado (puede venir en respuesta OK o error)
      if (result.isDuplicate) {
        const errorMsg = result.message || 'Documento ya existe en el sistema'
        console.log('Documento duplicado detectado:', errorMsg)
        setDuplicateError({
          message: errorMsg,
          docId: result.docId || ''
        })
        setUploadStatus({ message: `Error: ${errorMsg}`, type: 'error' })
        if (onError) onError(errorMsg)
        setIsLoading(false)
        setLoadingStep('')
        return
      }

      if (response.ok) {
        if (result.success === false) {
          console.log('Response OK pero success=false:', result)

          const errorMsg =
            result.error?.message ||
            result.error?.code ||
            'Error en el procesamiento'
          setUploadStatus({ message: `Error: ${errorMsg}`, type: 'error' })
          if (onError) onError(errorMsg)
          return
        }

        setDocId(result.doc_id || null)

        let facturaId = null

        if (result.dataset_path) {
          facturaId = result.dataset_path.split('/').pop()
          console.log('ID extraído de dataset_path:', facturaId)
        }

        if (!facturaId) {
          facturaId = result.id || result.facturaId || result.data?.id
          console.log('ID fallback:', facturaId)
        }

        if (facturaId) {
          console.log('Cargando editor para ID:', facturaId)

          setLoadingStep('ready')
          setUploadStatus({
            message: '✓ ¡Listo! Datos extraídos correctamente',
            type: 'success'
          })

          if (result.pdf_url) {
            setCurrentPdfUrl(result.pdf_url)
            console.log('PDF URL guardada:', result.pdf_url)
          } else {
            setCurrentPdfUrl(null)
            console.log('No se proporcionó URL del PDF en la respuesta')
          }
          setTimeout(() => {
            loadFacturaEditor(facturaId, result.campos)
          }, 1000)
        } else {
          console.log('No se pudo extraer ID de la respuesta')
          const errorMsg = `No se pudo obtener el ID de la factura. Respuesta: ${JSON.stringify(
            result
          )}`
          setUploadStatus({ message: `Error: ${errorMsg}`, type: 'error' })
          if (onError) onError(errorMsg)
        }
      } else {
        console.log('Error response (status no ok):', result)

        let errorMsg = 'Error desconocido'

        if (result.success === false && result.error) {
          errorMsg =
            result.error.message ||
            result.error.code ||
            'Error en el procesamiento'
        } else {
          errorMsg =
            result.data?.details ||
            result.message ||
            result.error ||
            'Error desconocido'
        }

        setUploadStatus({ message: `Error: ${errorMsg}`, type: 'error' })
        if (onError) onError(errorMsg)
      }
    } catch (error) {
      console.log('Catch error:', error)
      setLoadingStep('')
      const errorMsg = 'Error al subir el archivo'
      setUploadStatus({ message: errorMsg, type: 'error' })
      if (onError) onError(errorMsg)
      setIsLoading(false)
    }
  }

  // Cargar editor de factura
  const loadFacturaEditor = async (id: string, predData?: any) => {
    console.log('Iniciando loadFacturaEditor con ID:', id)
    setCurrentFacturaId(id)

    try {
      console.log('Cargando datos al formulario...')

      if (predData) {
        console.log(
          'Usando datos de predicción pasados como parámetro:',
          predData
        )
        loadDataToForm(predData)
      } else if (currentPredData) {
        console.log('Usando datos de predicción existentes:', currentPredData)
        loadDataToForm(currentPredData)
      } else {
        console.log('Intentando cargar datos desde endpoint...')
        try {
          const response = await fetch(
            `${api}/facturas/${encodeURIComponent(id)}`
          )
          const data = await response.json()
          setCurrentPredData(data.pred)
          const dataToLoad = data.gold || data.pred || {}
          loadDataToForm(dataToLoad)
        } catch (fetchError) {
          console.warn('No se pudieron cargar datos adicionales:', fetchError)
        }
      }

      console.log('Mostrando sección del editor...')
      setShowEditor(true)
      console.log('Editor cargado exitosamente')
    } catch (error) {
      console.error('Error en loadFacturaEditor:', error)
      const errorMsg = 'Error al cargar los datos de la factura'
      setUploadStatus({ message: errorMsg, type: 'error' })
      if (onError) onError(errorMsg)
    }
  }

  // Cargar datos al formulario
  const loadDataToForm = (data: any) => {
    // Primero actualizar el estado de predicción
    setCurrentPredData(data)

    // Cargar items si existen
    if (data.items && Array.isArray(data.items) && data.items.length > 0) {
      setItems(data.items)
    } else {
      setItems([])
    }

    // Forzar re-render del formulario con los nuevos datos
    if (formRef.current) {
      // Resetear y esperar un tick para que React actualice
      formRef.current.reset()

      // Cargar valores manualmente en los selects
      setTimeout(() => {
        if (formRef.current) {
          const form = formRef.current as HTMLFormElement

          // Comprobante
          if (data.comprobante?.tipo) {
            const tipoSelect = form.querySelector(
              '[name="comprobante.tipo"]'
            ) as HTMLSelectElement
            if (tipoSelect) tipoSelect.value = data.comprobante.tipo
          }
          if (data.comprobante?.letra) {
            const letraSelect = form.querySelector(
              '[name="comprobante.letra"]'
            ) as HTMLSelectElement
            if (letraSelect) letraSelect.value = data.comprobante.letra
          }

          // Emisor
          if (data.emisor?.condicion_iva) {
            const ivaSelect = form.querySelector(
              '[name="emisor.condicion_iva"]'
            ) as HTMLSelectElement
            if (ivaSelect) ivaSelect.value = data.emisor.condicion_iva
          }
        }
      }, 0)
    }

    // Activar efectos de carga completada
    setIsLoading(false)
    setIsDataLoaded(true)
    setTimeout(() => setIsDataLoaded(false), 2000)
  }

  // Recargar predicción
  const handleLoadPred = () => {
    if (currentPredData) {
      setShowUndoConfirm(true)
    }
  }

  // Confirmar deshacer cambios
  const confirmUndoChanges = () => {
    setShowUndoConfirm(false)
    loadDataToForm(currentPredData)
    setSaveStatus({
      message: '✓ Datos restaurados desde la predicción original',
      type: 'success'
    })
    setTimeout(() => setSaveStatus({ message: '', type: '' }), 5000)
  }

  // Recopilar datos del formulario
  const collectFormData = (): FacturaData => {
    const formData = new FormData(formRef.current!)

    return {
      comprobante: {
        tipo: (formData.get('comprobante.tipo') as string) || null,
        letra: (formData.get('comprobante.letra') as string) || null,
        punto_venta:
          (formData.get('comprobante.punto_venta') as string) || null,
        numero: (formData.get('comprobante.numero') as string) || null,
        fecha: (formData.get('comprobante.fecha') as string) || null
      },
      emisor: {
        cuit: cleanCuit(formData.get('emisor.cuit') as string),
        razon_social: (formData.get('emisor.razon_social') as string) || null,
        condicion_iva: (formData.get('emisor.condicion_iva') as string) || null
      },
      receptor: {
        cuit: cleanCuit(formData.get('receptor.cuit') as string)
      },
      importes: {
        neto: formData.get('importes.neto')
          ? parseFloat(formData.get('importes.neto') as string)
          : null,
        iva_21: formData.get('importes.iva_21')
          ? parseFloat(formData.get('importes.iva_21') as string)
          : null,
        iva_105: formData.get('importes.iva_105')
          ? parseFloat(formData.get('importes.iva_105') as string)
          : null,
        otros_impuestos: formData.get('importes.otros_impuestos')
          ? parseFloat(formData.get('importes.otros_impuestos') as string)
          : null,
        total: formData.get('importes.total')
          ? parseFloat(formData.get('importes.total') as string)
          : null
      },
      afip: {
        cae: (formData.get('afip.cae') as string) || null,
        cae_vencimiento:
          (formData.get('afip.cae_vencimiento') as string) || null
      },
      items: items
    }
  }

  // Validar datos del formulario
  const validateFormData = (data: FacturaData): string[] => {
    const errors: string[] = []

    // Validar Comprobante (todos obligatorios)
    if (!data.comprobante.tipo || data.comprobante.tipo.trim() === '') {
      errors.push('El campo "Tipo" de comprobante es obligatorio')
    }
    if (!data.comprobante.letra || data.comprobante.letra.trim() === '') {
      errors.push('El campo "Letra" de comprobante es obligatorio')
    }
    if (
      !data.comprobante.punto_venta ||
      data.comprobante.punto_venta.trim() === ''
    ) {
      errors.push('El campo "Punto venta" es obligatorio')
    }
    if (!data.comprobante.numero || data.comprobante.numero.trim() === '') {
      errors.push('El campo "Número" de comprobante es obligatorio')
    }
    if (!data.comprobante.fecha || data.comprobante.fecha.trim() === '') {
      errors.push('El campo "Fecha" de comprobante es obligatorio')
    }

    // Validar Emisor (todos obligatorios)
    if (!data.emisor.cuit || data.emisor.cuit.trim() === '') {
      errors.push('El campo "CUIT" del emisor es obligatorio')
    } else if (!/^\d{11}$/.test(data.emisor.cuit)) {
      errors.push('El CUIT del emisor debe tener exactamente 11 dígitos')
    }
    if (!data.emisor.condicion_iva || data.emisor.condicion_iva.trim() === '') {
      errors.push('El campo "Condición IVA" del emisor es obligatorio')
    }
    if (!data.emisor.razon_social || data.emisor.razon_social.trim() === '') {
      errors.push('El campo "Razón Social" del emisor es obligatorio')
    }

    // Validar Receptor y AFIP
    if (!data.afip.cae || data.afip.cae.trim() === '') {
      errors.push('El campo "CAE" es obligatorio')
    }
    if (!data.afip.cae_vencimiento || data.afip.cae_vencimiento.trim() === '') {
      errors.push('El campo "Vencimiento CAE" es obligatorio')
    }

    // Validar Items (al menos uno con concepto y subtotal)
    if (!data.items || data.items.length === 0) {
      errors.push('Debe agregar al menos un item/concepto')
    } else {
      const validItems = data.items.filter(
        (item) =>
          item.concepto && item.concepto.trim() !== '' && item.subtotal !== null
      )

      if (validItems.length === 0) {
        errors.push(
          'Debe haber al menos un item con "Concepto" y "Subtotal" completos'
        )
      }

      // Validar que cada item tenga concepto y subtotal
      for (let i = 0; i < data.items.length; i++) {
        const item = data.items[i]
        if (item.concepto && item.concepto.trim() !== '') {
          if (item.subtotal === null || isNaN(item.subtotal)) {
            errors.push(
              `El item ${i + 1
              } tiene concepto pero falta el subtotal o no es un número válido`
            )
          }
        }
      }
    }

    // Validar Importes
    if (data.importes.neto === null || isNaN(data.importes.neto)) {
      errors.push(
        'El campo "Neto" en importes es obligatorio y debe ser un número válido'
      )
    }
    if (data.importes.total === null || isNaN(data.importes.total)) {
      errors.push(
        'El campo "Total" en importes es obligatorio y debe ser un número válido'
      )
    }

    return errors
  }

  // Guardar cambios
  const handleSaveGold = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!currentFacturaId) return

    try {
      const data = collectFormData()

      // Validar datos antes de enviar
      const errors = validateFormData(data)
      if (errors.length > 0) {
        setValidationErrors(errors)
        return
      }

      setSaveStatus({ message: 'Guardando...', type: 'loading' })

      const response = await fetch(
        `${api}/facturas/${encodeURIComponent(currentFacturaId)}/gold`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }
      )

      if (response.ok) {
        setShowSuccessPopup(true)
        if (onFacturaSaved && currentPdfFile) {
          onFacturaSaved(currentFacturaId, data, currentPdfFile)
        }
      } else {
        setSaveStatus({
          message:
            '❌ Error al guardar los datos. Por favor, intentá nuevamente.',
          type: 'error'
        })
      }
    } catch (error) {
      setSaveStatus({
        message:
          '❌ Error al guardar los datos. Por favor, intentá nuevamente.',
        type: 'error'
      })
    }
  }

  // Manejar cierre de popup de éxito
  const handleSuccessClose = () => {
    setShowSuccessPopup(false)
    setSaveStatus({ message: '', type: '' })
    handleNewUpload()
  }

  // Nueva factura
  const handleNewUpload = () => {
    setShowEditor(false)
    setUploadStatus({ message: '', type: '' })
    if (pdfFileRef.current) pdfFileRef.current.value = ''
    setCurrentFacturaId(null)
    setCurrentPredData(null)
    setCurrentPdfUrl(null)
    setCurrentPdfFile(null)
    setItems([
      { concepto: null, cantidad: null, precio_unit: null, subtotal: null }
    ])
    setIsLoading(false)
    setIsDataLoaded(false)
    setLoadingStep('')
  }

  // Agregar item
  const handleAddItem = () => {
    setItems([
      ...items,
      { concepto: null, cantidad: null, precio_unit: null, subtotal: null }
    ])
  }

  // Eliminar item
  const handleRemoveItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index))
  }

  // Actualizar item
  const handleUpdateItem = (
    index: number,
    field: keyof ItemData,
    value: string
  ) => {
    const newItems = [...items]
    if (field === 'concepto') {
      newItems[index][field] = value || null
    } else {
      newItems[index][field] = value ? parseFloat(value) : null
    }
    setItems(newItems)
  }

  // Utilidades para URLs
  const isPresignedUrl = (url: string | null): boolean => {
    if (!url) return false
    return (
      url.includes('X-Amz-Algorithm') ||
      url.includes('AWSAccessKeyId') ||
      url.includes('Signature')
    )
  }

  const addCacheBusting = (url: string): string => {
    if (!url || isPresignedUrl(url)) {
      return url
    }
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}t=${Date.now()}`
  }

  // Renderizar vista previa del PDF
  const renderPdfPreview = () => {
    if (!currentFacturaId) return null

    const baseUrl = `${api}/facturas/${encodeURIComponent(
      currentFacturaId
    )}/file`

    if (currentPdfUrl) {
      return (
        <iframe
          src={currentPdfUrl}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title='Factura PDF'
        />
      )
    }

    // Fallback a imagen de preview o PDF original
    return (
      <iframe
        src={addCacheBusting(`${baseUrl}/original.pdf`)}
        style={{ width: '100%', height: '100%', border: 'none' }}
        title='Factura PDF'
      />
    )
  }

  // Helper para determinar si un campo tiene error
  const hasFieldError = (fieldName: string): boolean => {
    return validationErrors.some((error) =>
      error.toLowerCase().includes(fieldName.toLowerCase())
    )
  }

  return (
    <div className='facturas-editor'>
      {/* Guia Popup */}
      {showGuia && <GuiaCard onClose={() => setShowGuia(false)} />}

      {/* Duplicate Error Popup */}
      {duplicateError && (
        <div className='duplicate-overlay' onClick={() => setDuplicateError(null)}>
          <div className='duplicate-popup' onClick={(e) => e.stopPropagation()}>
            <div className='duplicate-header'>
              <h3>⚠️ Documento Duplicado</h3>
              <button className='duplicate-close' onClick={() => setDuplicateError(null)}>✕</button>
            </div>
            <div className='duplicate-content'>
              <div className='duplicate-icon'>📄</div>
              <p className='duplicate-message'>{duplicateError.message}</p>
              <p className='duplicate-hint'>Este documento ya fue procesado anteriormente en el sistema.</p>
            </div>
            <div className='duplicate-footer'>
              <button className='duplicate-ok-button' onClick={() => setDuplicateError(null)}>
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Undo Changes Confirmation Popup */}
      {showUndoConfirm && (
        <div className='confirm-overlay' onClick={() => setShowUndoConfirm(false)}>
          <div className='confirm-popup' onClick={(e) => e.stopPropagation()}>
            <div className='confirm-header'>
              <h3>⚠️ Deshacer Cambios</h3>
              <button className='confirm-close' onClick={() => setShowUndoConfirm(false)}>✕</button>
            </div>
            <div className='confirm-content'>
              <div className='confirm-icon'>↩️</div>
              <p className='confirm-message'>¿Estás seguro de deshacer todos los cambios?</p>
              <p className='confirm-hint'>Se restaurarán los datos originales extraídos por la IA. Esta acción no se puede revertir.</p>
            </div>
            <div className='confirm-footer'>
              <button className='confirm-cancel-button' onClick={() => setShowUndoConfirm(false)}>
                Cancelar
              </button>
              <button className='confirm-ok-button' onClick={confirmUndoChanges}>
                Sí, deshacer cambios
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup */}
      {showSuccessPopup && (
        <div className='success-overlay' onClick={handleSuccessClose}>
          <div className='success-popup' onClick={(e) => e.stopPropagation()}>
            <div className='success-header'>
              <h3>✓ Factura Guardada</h3>
              <button className='success-close' onClick={handleSuccessClose}>✕</button>
            </div>
            <div className='success-content'>
              <div className='success-icon'>✅</div>
              <p className='success-message'>¡Factura guardada exitosamente!</p>
              <p className='success-hint'>Los datos han sido enviados correctamente al sistema.</p>
            </div>
            <div className='success-footer'>
              <button className='success-ok-button' onClick={handleSuccessClose}>
                Cargar nueva factura
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Validation Errors Popup */}
      {validationErrors.length > 0 && (
        <div
          className='validation-overlay'
          onClick={() => setValidationErrors([])}
        >
          <div
            className='validation-popup'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='validation-header'>
              <h3>⚠️ Errores de Validación</h3>
              <button
                className='validation-close'
                onClick={() => setValidationErrors([])}
              >
                ✕
              </button>
            </div>
            <div className='validation-content'>
              <p className='validation-intro'>
                Por favor, corregí los siguientes errores antes de guardar:
              </p>
              <ul className='validation-list'>
                {validationErrors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
            <div className='validation-footer'>
              <button
                className='validation-ok-button'
                onClick={() => setValidationErrors([])}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className='facturas-header'>
        <div className='header-content'>
          <div className='header-title'>
            <h1>Carga de Facturas</h1>
          </div>
          <div className='header-steps'>
            <div className={`step ${!showEditor ? 'active' : 'completed'}`}>
              <span className='step-number'>1</span>
              <span className='step-label'>Cargar PDF</span>
            </div>
            <div className='step-divider'>→</div>
            <div
              className={`step ${showEditor && !saveStatus.message
                ? 'active'
                : saveStatus.type === 'success'
                  ? 'completed'
                  : ''
                }`}
            >
              <span className='step-number'>2</span>
              <span className='step-label'>Verificar datos</span>
            </div>
            <div className='step-divider'>→</div>
            <div
              className={`step ${saveStatus.type === 'success' ? 'completed' : ''
                }`}
            >
              <span className='step-number'>3</span>
              <span className='step-label'>Confirmar</span>
            </div>
          </div>

          <div className='header-right'>
            {cuit && <div className='cuit-badge'>CUIT: {cuit}</div>}
            <div className='system-status'>
              <span
                className={`status-indicator ${apiStatus === 'ok' ? 'online' : 'offline'
                  }`}
              ></span>
              <span className='status-text'>
                {apiStatus === 'ok' ? 'Sistema online' : 'Sistema offline'}
              </span>
            </div>

            <div className='help-menu-container'>
              <button
                className='help-button'
                onClick={() => setShowHelpMenu(!showHelpMenu)}
                onBlur={() => setTimeout(() => setShowHelpMenu(false), 200)}
              >
                ?
              </button>
              {showHelpMenu && (
                <div className='help-dropdown'>
                  <button
                    className='help-item'
                    onClick={() => {
                      setShowGuia(true)
                      setShowHelpMenu(false)
                    }}
                  >
                    📖 Guía de carga
                  </button>
                  {soporte && (
                    <div
                      className='help-item'
                      style={{
                        cursor: 'default',
                        whiteSpace: 'normal',
                        padding: '0.5rem',
                        fontSize: '0.9rem'
                      }}
                    >
                      💬 {soporte}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className='facturas-main'>
        <section className='editor-section'>
          <div className='editor-layout'>
            {/* Panel izquierdo: Drag & Drop o PDF */}
            <div className='image-panel'>
              {!showEditor ? (
                <div className='upload-container'>
                  <div className='upload-title'>
                    <h2>Subí tu Factura en PDF</h2>
                    <p className='upload-subtitle'>
                      Nuestra IA extraerá los datos automáticamente
                    </p>
                  </div>

                  {apiStatus !== 'ok' && (
                    <div className='offline-notice'>
                      <div className='offline-icon'>⚠️</div>
                      <h3>Sistema Temporalmente Deshabilitado</h3>
                      <p>La carga de facturas no está disponible en este momento.</p>
                      <p className='offline-hint'>Por favor, intentá nuevamente más tarde.</p>
                    </div>
                  )}

                  <div
                    className={`dropzone ${isDragging ? 'dragging' : ''} ${apiStatus !== 'ok' || isLoading ? 'disabled' : ''}`}
                    onDragEnter={apiStatus === 'ok' && !isLoading ? handleDragEnter : undefined}
                    onDragOver={apiStatus === 'ok' && !isLoading ? handleDragOver : undefined}
                    onDragLeave={apiStatus === 'ok' && !isLoading ? handleDragLeave : undefined}
                    onDrop={apiStatus === 'ok' && !isLoading ? handleDrop : undefined}
                    onClick={apiStatus === 'ok' && !isLoading ? handleFileClick : undefined}
                  >
                    <div className='dropzone-content'>
                      <svg
                        className='dropzone-icon'
                        viewBox='0 0 24 24'
                        fill='none'
                        stroke='currentColor'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12'
                        />
                      </svg>
                      <p className='dropzone-text'>
                        <button type='button' className='dropzone-button'>
                          Seleccionar archivo PDF
                        </button>
                      </p>
                      <p className='dropzone-hint'>o arrastra y suelta aquí</p>
                    </div>
                    <input
                      type='file'
                      ref={pdfFileRef}
                      accept='application/pdf'
                      disabled={apiStatus !== 'ok' || isLoading}
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleUploadSubmit(e as any)
                        }
                      }}
                      style={{ display: 'none' }}
                    />
                  </div>

                  <div className='upload-info'>
                    <div className='upload-tips'>
                      <p className='upload-tips-title'>💡 Consejos:</p>
                      <p>✓ Asegurate de que el PDF sea legible</p>
                      <p>✓ Debe contener todos los datos fiscales</p>
                      <p>✓ Tamaño máximo: 10MB</p>
                    </div>
                  </div>

                  {uploadStatus.message && (
                    <div className={`result-message ${uploadStatus.type}`}>
                      {uploadStatus.message}
                    </div>
                  )}
                </div>
              ) : (
                <div className='pdf-container'>
                  <div className='pdf-header'>
                    <h3>📄 Tu Factura</h3>
                  </div>
                  {renderPdfPreview()}
                </div>
              )}
            </div>

            {/* Panel derecho: Formulario siempre visible */}
            <div className='form-panel'>
              <div className='form-panel-header'>
                <div>
                  <h3>✓ Verificá los Datos Extraídos</h3>
                  <p className='form-panel-subtitle'>
                    Revisá que la información sea correcta antes de enviar
                  </p>
                </div>
                {showEditor && (
                  <div className='extraction-badge'>
                    <span className='badge-icon'>🤖</span>
                    <span className='badge-text'>
                      Datos extraídos automáticamente
                    </span>
                  </div>
                )}
              </div>

              <form ref={formRef} onSubmit={handleSaveGold}>
                <fieldset
                  disabled={!showEditor}
                  className={
                    isLoading ? 'loading' : isDataLoaded ? 'data-loaded' : ''
                  }
                >
                  {/* Comprobante */}
                  <fieldset className='form-card'>
                    <legend>
                      <span className='legend-icon'>🧾</span> Comprobante
                    </legend>
                    <div className='form-row-comprobante'>
                      <label>
                        Tipo:
                        <select
                          name='comprobante.tipo'
                          defaultValue={
                            currentPredData?.comprobante?.tipo || ''
                          }
                          className={hasFieldError('tipo') ? 'field-error' : ''}
                        >
                          <option value=''>Seleccionar</option>
                          <option value='Factura'>Factura</option>
                          <option value='Nota de Crédito'>
                            Nota de Crédito
                          </option>
                          <option value='Nota de Débito'>Nota de Débito</option>
                          <option value='Recibo'>Recibo</option>
                        </select>
                      </label>
                      <label>
                        Letra:
                        <select
                          name='comprobante.letra'
                          defaultValue={
                            currentPredData?.comprobante?.letra || ''
                          }
                          className={
                            hasFieldError('letra') ? 'field-error' : ''
                          }
                        >
                          <option value=''>Seleccionar</option>
                          <option value='A'>A</option>
                          <option value='B'>B</option>
                          <option value='C'>C</option>
                          <option value='E'>E</option>
                          <option value='M'>M</option>
                          <option value='T'>T</option>
                        </select>
                      </label>
                      <label>
                        Punto venta:
                        <input
                          type='text'
                          name='comprobante.punto_venta'
                          defaultValue={
                            currentPredData?.comprobante?.punto_venta || ''
                          }
                          placeholder='0001'
                          className={
                            hasFieldError('punto venta') ? 'field-error' : ''
                          }
                        />
                      </label>
                      <label>
                        Número:
                        <input
                          type='text'
                          name='comprobante.numero'
                          defaultValue={
                            currentPredData?.comprobante?.numero || ''
                          }
                          placeholder='00000001'
                          className={
                            hasFieldError('número') &&
                              hasFieldError('comprobante')
                              ? 'field-error'
                              : ''
                          }
                        />
                      </label>
                      <label>
                        Fecha:
                        <input
                          type='date'
                          name='comprobante.fecha'
                          defaultValue={
                            currentPredData?.comprobante?.fecha || ''
                          }
                          className={
                            hasFieldError('fecha') ? 'field-error' : ''
                          }
                        />
                      </label>
                    </div>
                  </fieldset>

                  {/* Emisor */}
                  <fieldset className='form-card'>
                    <legend>
                      <span className='legend-icon'>🏢</span> Emisor
                    </legend>
                    <div className='form-row-emisor'>
                      <label>
                        CUIT:
                        <input
                          type='text'
                          name='emisor.cuit'
                          defaultValue={currentPredData?.emisor?.cuit || ''}
                          pattern='[0-9]{11}'
                          maxLength={11}
                          title='Exactamente 11 dígitos numéricos'
                          className={
                            hasFieldError('cuit') && hasFieldError('emisor')
                              ? 'field-error'
                              : ''
                          }
                        />
                      </label>
                      <label>
                        Condición IVA:
                        <select
                          name='emisor.condicion_iva'
                          defaultValue={
                            currentPredData?.emisor?.condicion_iva || ''
                          }
                          className={
                            hasFieldError('condición iva') ? 'field-error' : ''
                          }
                        >
                          <option value=''>Seleccionar</option>
                          <option value='IVA Responsable Inscripto'>
                            IVA Responsable Inscripto
                          </option>
                          <option value='IVA Responsable No Inscripto'>
                            IVA Responsable No Inscripto
                          </option>
                          <option value='IVA Sujeto Exento'>
                            IVA Sujeto Exento
                          </option>
                          <option value='Consumidor Final'>
                            Consumidor Final
                          </option>
                          <option value='Responsable Monotributo'>
                            Responsable Monotributo
                          </option>
                          <option value='Proveedor del Exterior'>
                            Proveedor del Exterior
                          </option>
                          <option value='Cliente del Exterior'>
                            Cliente del Exterior
                          </option>
                          <option value='IVA Liberado - Ley 19.640'>
                            IVA Liberado - Ley 19.640
                          </option>
                          <option value='Monotributista Social'>
                            Monotributista Social
                          </option>
                          <option value='IVA No Alcanzado'>
                            IVA No Alcanzado
                          </option>
                        </select>
                      </label>
                      <label>
                        Razón Social:
                        <input
                          type='text'
                          name='emisor.razon_social'
                          defaultValue={
                            currentPredData?.emisor?.razon_social || ''
                          }
                          className={
                            hasFieldError('razón social') ? 'field-error' : ''
                          }
                        />
                      </label>
                    </div>
                  </fieldset>

                  {/* Receptor y AFIP */}
                  <fieldset className='form-card'>
                    <legend>
                      <span className='legend-icon'>📋</span> Receptor y AFIP
                    </legend>
                    <div className='form-row-receptor-afip'>
                      <label>
                        CUIT Receptor:
                        <input
                          type='text'
                          name='receptor.cuit'
                          defaultValue={currentPredData?.receptor?.cuit || ''}
                          pattern='[0-9]{11}'
                          maxLength={11}
                          title='Exactamente 11 dígitos numéricos'
                        />
                      </label>
                      <label>
                        CAE:
                        <input
                          type='text'
                          name='afip.cae'
                          defaultValue={currentPredData?.afip?.cae || ''}
                          className={
                            hasFieldError('cae') &&
                              !hasFieldError('vencimiento')
                              ? 'field-error'
                              : ''
                          }
                        />
                      </label>
                      <label>
                        Vencimiento CAE:
                        <input
                          type='date'
                          name='afip.cae_vencimiento'
                          defaultValue={
                            currentPredData?.afip?.cae_vencimiento || ''
                          }
                          className={
                            hasFieldError('vencimiento cae')
                              ? 'field-error'
                              : ''
                          }
                        />
                      </label>
                    </div>
                  </fieldset>

                  {/* Items */}
                  <fieldset className='form-card'>
                    <legend>
                      <span className='legend-icon'>📦</span> Items / Conceptos
                    </legend>
                    <div className='items-container'>
                      {items.map((item, index) => (
                        <div key={index} className='item'>
                          <div className='form-row-item'>
                            <label>
                              Concepto:
                              <input
                                type='text'
                                value={item.concepto || ''}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    index,
                                    'concepto',
                                    e.target.value
                                  )
                                }
                              />
                            </label>
                            <label>
                              Cantidad:
                              <input
                                type='number'
                                step='0.01'
                                min='0'
                                value={item.cantidad || ''}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    index,
                                    'cantidad',
                                    e.target.value
                                  )
                                }
                              />
                            </label>
                            <label>
                              Precio unit.:
                              <input
                                type='number'
                                step='0.01'
                                min='0'
                                value={item.precio_unit || ''}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    index,
                                    'precio_unit',
                                    e.target.value
                                  )
                                }
                              />
                            </label>
                            <label>
                              Subtotal:
                              <input
                                type='number'
                                step='0.01'
                                min='0'
                                value={item.subtotal || ''}
                                onChange={(e) =>
                                  handleUpdateItem(
                                    index,
                                    'subtotal',
                                    e.target.value
                                  )
                                }
                              />
                            </label>
                            <button
                              type='button'
                              className='btn-remove-item'
                              onClick={() => handleRemoveItem(index)}
                              title='Eliminar item'
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      type='button'
                      className='btn-secondary'
                      onClick={handleAddItem}
                    >
                      Agregar Item
                    </button>
                  </fieldset>

                  {/* Importes */}
                  <fieldset className='form-card'>
                    <legend>
                      <span className='legend-icon'>💰</span> Importes
                    </legend>
                    <div className='form-row-importes'>
                      <label>
                        Neto:
                        <input
                          type='number'
                          name='importes.neto'
                          defaultValue={currentPredData?.importes?.neto || ''}
                          step='0.01'
                          min='0'
                          className={hasFieldError('neto') ? 'field-error' : ''}
                        />
                      </label>
                      <label>
                        IVA 21%:
                        <input
                          type='number'
                          name='importes.iva_21'
                          defaultValue={currentPredData?.importes?.iva_21 || ''}
                          step='0.01'
                          min='0'
                        />
                      </label>
                      <label>
                        IVA 10.5%:
                        <input
                          type='number'
                          name='importes.iva_105'
                          defaultValue={
                            currentPredData?.importes?.iva_105 || ''
                          }
                          step='0.01'
                          min='0'
                        />
                      </label>
                      <label>
                        Otros imp.:
                        <input
                          type='number'
                          name='importes.otros_impuestos'
                          defaultValue={
                            currentPredData?.importes?.otros_impuestos || ''
                          }
                          step='0.01'
                          min='0'
                        />
                      </label>
                      <label>
                        Total:
                        <input
                          type='number'
                          name='importes.total'
                          defaultValue={currentPredData?.importes?.total || ''}
                          step='0.01'
                          min='0'
                          className={
                            hasFieldError('total') ? 'field-error' : ''
                          }
                        />
                      </label>
                    </div>
                  </fieldset>
                  <div className='form-actions'>
                    <button
                      type='button'
                      onClick={handleLoadPred}
                      disabled={!showEditor}
                    >
                      Deshacer cambios
                    </button>
                    <button type='submit' disabled={!showEditor}>
                      Guardar cambios
                    </button>
                  </div>

                </fieldset>
                {saveStatus.message && (
                  <div className={`status-message ${saveStatus.type}`}>
                    {saveStatus.message}
                  </div>
                )}
              </form>
              <fieldset
                disabled={!showEditor}
                className={
                  isLoading ? 'loading' : isDataLoaded ? 'data-loaded' : ''
                }
              >
                <FacturasAttachments
                  facturaId={docId!}
                  maxFileSize={50 * 1024 * 1024}
                  maxAttachments={20}
                  onAttachmentAdded={(attachment) => {
                    console.log('Adjunto agregado:', attachment);
                  }}
                  onAttachmentDeleted={(id) => {
                    console.log('Adjunto eliminado:', id);
                  }}
                  onError={(error) => {
                    console.error('Error:', error);
                  }}
                />
              </fieldset>
            </div>
          </div>
        </section>
      </main>
    </div>
  )
}

export default FacturasEditor
